// WebXrWheelRenderer — production WheelRenderer impl.
//
// Ports the four highest-priority techniques from the legacy WebAR catalog
// (see docs.autoviz.io/internal/docs/services-strategy/ato/legacy-webar-catalog-of-techniques.md):
//
// 1. Wall-as-local-frame — first placeWheel auto-commits a 100×100×0.01
//    invisible plane at the hit pose, oriented to face the camera. All
//    subsequent placement raycasts target this wall, NOT XR8.hitTest. The
//    wall absorbs SLAM jitter because it's fixed in world space and the
//    raycast onto it is deterministic.
// 2. fixedPos canonical state — each placed wheel stores `fixedPos: Vector3`
//    as the source of truth. adjustWheel + setWheelScale update fixedPos;
//    Object3D.position lerps toward it.
// 3. lerp(0.1) damping — tick() runs every XR8 frame (driver.onUpdate) and
//    smooths Object3D.position toward fixedPos. Quaternion + scale apply
//    immediately (no jitter source on those axes).
// 4. Wall raycast for placement — hitTest() raycasts against the local
//    wallMesh once committed; only the FIRST tap (no wall yet) falls
//    through to driver.hitTest (XR8). This breaks the per-frame XR8
//    dependency that was the root cause of wheels jumping.

import * as THREE from 'three'
import {
  arSessionState,
  DEFAULT_MANUAL_WHEEL_ADJUSTMENTS,
  type ARSessionState,
  type ManualWheelAdjustments,
} from '@/models/placement-state'
import {
  wheelScaleAxisVector,
  type WheelScale,
} from '@/models/wheel-scale'
import type {
  HitResult,
  PlacedWheel,
  ScreenPoint,
  WheelId,
  WheelModelInput,
  WheelModelMeasurement,
  WheelRenderer,
} from '@/ar/wheel-renderer'
import {
  createWheelBarrelOccluder,
  deriveWheelDimensions,
  buildTireBandRing,
} from '@/ar/wheel-barrel-occluder'
import { SceneLighting } from '@/ar/scene-lighting'
import {
  chooseDepthPlacement,
  pushBackFromCamera,
  resolveContactLift,
  DEPTH_PUSH_BACK_M,
  DEPTH_TWEEN_TAU_MS,
  TIRE_CLEARANCE_M,
} from '@/ar/depth-placement'
import {
  updateFloorEstimate,
  isHorizontalSurface,
  type FloorEstimate,
} from '@/ar/floor-level'
import {
  buildSelectionRing,
  disposeSelectionRing,
  SELECTION_RING_NAME,
} from '@/ar/selection-ring'
import { buildIosSelectionArcs } from '@/ar/ios-selection-arcs'
import {
  sortWheelsFarToNear,
  renderOrderForWheel,
  DEPTH_RESTAMP_RENDER_ORDER,
} from '@/ar/wheel-render-order'
import { buildPlacementRing } from '@/ar/placement-ring'
import {
  yawNormalFromChord,
  yawNormalFromSurfaceNormal,
} from '@/ar/surface-yaw'
import { atoLog } from '@/services/ato-log'

/**
 * AR driver protocol — abstracts over the specific AR backend (XR8,
 * WebXR/ARCore, future engines). The renderer is decoupled from the
 * tracking source: same scene-graph code on top of any of them.
 *
 * Naming history: this was `XR8Driver` while the only impl was XR8;
 * renamed to `ARDriver` when WebXR was added.
 */
export interface ARDriver {
  start(opts: {
    canvas?: HTMLCanvasElement
    /** WebXR drivers need the scene to render every frame. XR8 drivers
     *  receive a scene from the engine via onSceneReady and may ignore
     *  this field. */
    threeScene: THREE.Scene
    threeCamera: THREE.PerspectiveCamera
    onSessionState: (state: ARSessionState) => void
    /** XR8 fires this once it has constructed its own scene/camera/renderer
     *  trio that the wheel renderer must adopt. WebXR does NOT swap scene
     *  or camera (we keep the renderer-owned ones), so this callback is
     *  never fired by the WebXR driver. */
    onSceneReady?: (xrScene: {
      scene: THREE.Scene
      camera: THREE.PerspectiveCamera
    }) => void
    /** Fires every AR frame so the renderer can run its lerp tick. */
    onUpdate?: () => void
    /** WebXR: fires once the driver has created the WebGLRenderer and
     *  before it calls renderer.xr.setSession(), so the renderer can
     *  install GL-bound lighting (PMREM baseline env + XREstimatedLight)
     *  that must subscribe to `sessionstart` before it fires. XR8 drivers
     *  don't fire this. */
    onRendererReady?: (renderer: THREE.WebGLRenderer) => void
  }): Promise<void>
  stop(): Promise<void>
  hitTest(point: ScreenPoint): HitResult | null
  /** Optional: ask the AR runtime to anchor `hit`'s world position to
   *  the physical world. Returns an opaque id the renderer stores on
   *  the placed node; each frame the renderer resamples the anchor's
   *  current world pose via getAnchorPosition and updates its stored
   *  basePosition. Under ARCore, this makes wheels survive re-
   *  localization on erratic phone motion — the anchor's world pose
   *  tracks the same physical spot as ARCore's world model updates.
   *  Legacy drivers (XR8) don't implement this; the renderer degrades
   *  to the pre-anchor behaviour (stored coord in the drifting frame). */
  createAnchor?(hit: HitResult): string | null
  /** Optional: resolve an anchor id to its current world position, or
   *  null if the runtime has lost tracking on it. Renderer holds the
   *  last known position when null. */
  getAnchorPosition?(id: string): { x: number; y: number; z: number } | null
  /** Optional: releases a previously-created anchor (frees the ARCore
   *  bookkeeping). Called by the renderer on wheel removal. */
  releaseAnchor?(id: string): void
  /** Preferred anchor path when a plane hit is available. Uses
   *  XRHitTestResult.createAnchor() → plane-attached anchor, which
   *  ARCore keeps glued to the underlying tracked plane. Returns
   *  the target world position for placement + an id used with
   *  getAnchorPosition. Returns null when no ARCore hit is cached
   *  — the renderer refuses placement in that case. */
  createPlaneAttachedAnchor?(): {
    id: string
    position: { x: number; y: number; z: number }
  } | null
  /** Optional: latest hit sample from a downward-angled hit-test source
   *  (used by the ?groundLine feature to freeze a ground Y coordinate
   *  from the first tap). Returns null when the driver did not create
   *  the angled source or the last frame produced no hit. */
  getGroundHit?(): HitResult | null
  /** Optional: latest center-pixel sample from the WebXR Depth API
   *  (camera depth map), as a world-space hit at the sensed distance
   *  along the viewer ray. Registers surfaces that are NOT detected
   *  planes — most importantly the car body. null when depth sensing
   *  was not requested / granted or the last frame had no usable
   *  sample. See @/ar/depth-placement for the placement policy. */
  getDepthHit?(): HitResult | null
  /** Optional (?surfaceYaw): the latest depth-map neighbourhood chord —
   *  two off-center samples unprojected to world space. Null when
   *  neighbourhood sampling is off or either sample was unusable. */
  getDepthChord?(): {
    left: { x: number; y: number; z: number }
    right: { x: number; y: number; z: number }
  } | null
  /** Optional (?surfaceYaw): the REAL world normal of the current
   *  cached plane hit. HitResult.normal is a hardcoded placeholder
   *  (see the driver's cachedHit comment); this is the pose-derived
   *  normal the driver already computes for surface classification. */
  getSurfaceHitNormal?(): { x: number; y: number; z: number } | null
}

/** @deprecated Use {@link ARDriver}. Kept as a type alias during the
 *  WebXR migration so external import sites compile. */
export type XR8Driver = ARDriver

export type GltfParser = (bytes: ArrayBuffer) => Promise<THREE.Group>

export interface WebXrWheelRendererConfig {
  driver: ARDriver
  gltfParser: GltfParser
  scene?: THREE.Scene
  camera?: THREE.PerspectiveCamera
  canvas?: HTMLCanvasElement
  /** Lerp alpha for fixedPos → render position damping. Default 0.1
   *  (legacy WebAR catalog technique 1.2). Test code may pass 1.0 to
   *  disable smoothing for instant assertions. */
  lerpAlpha?: number
  /** Default wall-commit distance in meters when no real surface is
   *  detected. Default 3m — far enough that a typical car-wheel-sized
   *  GLB (~0.6m diameter) renders at a reasonable on-screen size. */
  defaultWallDistanceMeters?: number
  /** When true, attaches a wireframe-grid child to the wall mesh so the
   *  wall pose is visible during development. Production builds should
   *  leave this off. */
  wallDebugVisible?: boolean
  /** Target diameter (meters) the wheel template is normalised to after
   *  GLB parse. Mirrors legacy catalog technique 4.1 — defends against
   *  GLBs authored at non-meter scale. Default 0.6m (~24" wheel). */
  templateDiameterMeters?: number
  /** DEBUG: when true, parents placed wheels directly to the THREE.Camera
   *  at a fixed local offset instead of placing them in world space. The
   *  wheel becomes screen-locked: no SLAM pose, no scene-graph math, no
   *  raycast can shift its on-screen position. Used as a baseline to
   *  diagnose whether observed jitter originates in SLAM or in the
   *  renderer / GLB. Default false. */
  cameraLockMode?: boolean
  /** ?depthPlace mode: placement consults the WebXR Depth API sample
   *  alongside the plane hit-test and prefers the depth sample when it
   *  is meaningfully in FRONT of the detected plane (the car-in-front-
   *  of-wall case — see @/ar/depth-placement). Default false: the
   *  plane hit-test path is byte-for-byte unchanged. */
  depthPlaceMode?: boolean
  /** ?ringPlace (2026-09-18) — two-step placement. While aiming, the
   *  preview is a plain circle instead of the wheel GLB; the user puts
   *  it around the rim of their real wheel and taps, and the wheel is
   *  dropped CENTRED on it. The ring replaces the contact-lift chain as
   *  the drop reference, so it is deliberately exclusive with the lift.
   *  Default OFF — a release toggle, not a rewrite. */
  ringPlaceMode?: boolean
  /** ?use_ios_ui (2026-10-17) — false: never attach the 3D aiming
   *  preview. The iOS UI aims with a 2D ring overlay and places from its
   *  Place button, so a preview wheel (and the gauge that follows it)
   *  would be a second, competing target. Default true — unchanged. */
  aimingPreview?: boolean
  /** ?use_ios_ui (2026-10-19) — 'ios': the iOS blue arcs on the rim
   *  (ios-selection-arcs) in place of the dashed halo. Default 'android'. */
  selectionRingStyle?: 'android' | 'ios'
  /** ?use_ios_ui (2026-10-20) — the framing ring is aimed at the RIM, so
   *  the wheel's CENTRE goes on the aimed 3D point: no floor swap and no
   *  contact lift (both exist to rest a tire on a ground line the user
   *  aims at; the ring replaces that reference). Default false. */
  framedPlacement?: boolean
  /** ?instantPlace: skip the preview→target drop animation — the
   *  placed wheel appears at its final pose immediately. Field-triage
   *  switch: rules the animation out as a perceived-displacement
   *  source. Default false (animated). */
  instantPlaceMode?: boolean
  /** ?depthPrepass — drop the oversized barrel-mask proxy and let real
   *  geometry drive occlusion, so the transparent gradient can depth-test
   *  against a truthful buffer. See createWheelBarrelOccluder. */
  depthPrepassMode?: boolean
  /** ?surfaceYaw — placement derives the wheel's yaw from the struck
   *  surface (depth chord / real plane normal) instead of facing the
   *  phone; falls back to view-aligned when nothing is derivable.
   *  See @/ar/surface-yaw. Default false (view-aligned, 7dad1e3). */
  surfaceYawMode?: boolean
  /** Injectable clock for tests; defaults to performance.now. */
  now?: () => number
}

interface PlacedNode {
  readonly id: WheelId
  readonly group: THREE.Group
  basePosition: THREE.Vector3
  baseNormal: THREE.Vector3
  scale: WheelScale
  adjustments: ManualWheelAdjustments
  /** Canonical anchor position. Object3D.position lerps toward this. */
  fixedPos: THREE.Vector3
  /** Opaque id returned by driver.createAnchor at placement time.
   *  When non-null, tick() polls driver.getAnchorPosition each frame
   *  and rewrites basePosition so the wheel tracks its anchor through
   *  ARCore re-localization. null when the driver doesn't support
   *  anchors (legacy XR8) or createAnchor returned null. */
  anchorId: string | null
  /** Known face radius (meters, pre-group-scale units) captured at
   *  build time — the dimension-based fallback for the contact lift. */
  faceRadiusM: number
  /** Model units per real metre for the template this wheel was built
   *  from — setWheelModel's normalisation, snapshotted because a later
   *  model can replace the template while this wheel is in the scene. */
  modelUnitsPerMetre: number
  /** True once the anchor's pose has ever been resolved. The
   *  visibility gate only hides the wheel when the anchor previously
   *  landed AND now has a null pose (real tracking gap). Before the
   *  first resolve, the wheel stays visible so it can animate from
   *  the preview position to the target while the async
   *  plane-attached anchor is still landing (~1 frame delay). */
  anchorLanded: boolean
}

// Wall mesh dimensions, matching the legacy catalog (technique 2.3). A
// 100×100m plane is large enough that any plausible raycast hits exactly
// once; the 0.01m Z-thickness keeps geometry stable while remaining a
// visually flat plane.
const WALL_WIDTH = 100
const WALL_HEIGHT = 100
const WALL_THICKNESS = 0.01

const DEFAULT_LERP_ALPHA = 0.1
const DEFAULT_WALL_DISTANCE_M = 3
const DEFAULT_TEMPLATE_DIAMETER_M = 0.6

// Camera-local Z offset (in meters) of the preview wheel. The wheel is
// attached as a child of the THREE.Camera at (0, 0, -PREVIEW_DISTANCE_M),
// putting it 1.5 m in front of the camera along the look direction.
//
// This distance is also the WebXR driver's placement distance — see
// computePlacementWorldPosition() in webxr-driver.ts. Preview and placed
// wheels MUST be at the same distance from the camera at the moment of
// tap so their VISIBLE (angular) size matches — otherwise the wheel
// appears to shrink/grow the instant the user taps.
export const PREVIEW_DISTANCE_M = 2

/** How long a preview-resolved placement hit stays valid for the tap.
 *  Covers the gap between the last preview tick and the tap landing
 *  (a few frames), without letting a genuinely stale aim get placed. */
export const PREVIEW_HIT_REUSE_MS = 600

/** How long the last DEPTH-sourced placement point survives a depth-map
 *  dropout. info-null comes in multi-frame bursts on the 14T Pro; while
 *  the burst lasts, falling back to the plane hit flicks the target
 *  ~0.5-1m deeper. A held depth point beats a wrong plane point for
 *  this window; past it, the aim has genuinely changed. */
export const DEPTH_HOLD_MS = 1500

export class WebXrWheelRenderer implements WheelRenderer {
  private readonly driver: ARDriver
  private readonly gltfParser: GltfParser
  private scene: THREE.Scene
  private camera: THREE.PerspectiveCamera
  private readonly canvas: HTMLCanvasElement | undefined
  /** Placement tracking mode. 'plane' (default) uses ARCore's plane-hit
   *  position + XRHitTestResult.createAnchor (plane-attached, current
   *  behaviour). 'world' overrides the placement point to
   *  camera_forward × PREVIEW_DISTANCE_M and uses a manufactured world
   *  anchor — the pre-plane-attached behaviour, which the user found
   *  visibly more stable under some lighting conditions. Toggled at
   *  runtime by the ?trackToggle HUD button. */
  private trackMode: 'plane' | 'world' = 'plane'
  /** Preview target mode. 'camera-locked' (default) parents the
   *  preview to the camera at (0, 0, -PREVIEW_DISTANCE_M) — screen-
   *  locked, zero aim-time parallax. 'plane-anchored' adds the
   *  preview to the scene and updates its world position each frame
   *  from the driver's hit-test — user sees exactly where the wheel
   *  will land (with the AR parallax that comes with a world-tracked
   *  object). Debug/UAT-only, toggled by the ?previewToggle button. */
  private previewMode: 'camera-locked' | 'plane-anchored' = 'plane-anchored'
  /** ?groundLine=1 mode. When on, the first placement raycasts through
   *  the downward-angled ground hit-test source to derive a fixed
   *  ground Y. Subsequent placements land at that same Y with a world
   *  anchor. When off, standard hit-test / anchor flow applies. */
  private groundLineMode = false
  /** Frozen ground Y from the first ?groundLine calibration tap. null
   *  before calibration; once set, every subsequent placement's Y is
   *  clamped to this value. */
  private groundY: number | null = null
  /** Sticky floor level shared with the placement gauge — the single
   *  answer to "how high is the ground?". See floor-level.ts. */
  private floorEstimate: FloorEstimate = null
  private readonly lerpAlpha: number
  // Deprecated — placement no longer uses a manufactured wall distance
  // fallback (no plane, no placement). Kept only so the config option
  // continues to type-check for callers that pass it.
  // @ts-expect-error unused
  private readonly _defaultWallDistance: number
  private readonly wallDebugVisible: boolean
  private readonly templateDiameter: number
  private readonly cameraLockMode: boolean
  /** ?depthPlace — placement consults the depth-map sample alongside
   *  the plane hit-test (see hitTest + @/ar/depth-placement). */
  private readonly depthPlaceMode: boolean

  /** ?ringPlace — see the config declaration. */
  private readonly ringPlaceMode: boolean
  /** ?use_ios_ui — see the config declaration. */
  private readonly aimingPreview: boolean
  /** ?use_ios_ui — see the config declaration. */
  private readonly selectionRingStyle: 'android' | 'ios'
  /** ?use_ios_ui — see the config declaration. */
  private readonly framedPlacement: boolean
  /** setWheelModel's normalisation factor (model units per authored
   *  metre). 1 until a model loads. */
  private modelUnitsPerMetre = 1
  /** ?instantPlace — no preview→target drop animation. */
  private readonly instantPlaceMode: boolean
  private readonly depthPrepassMode: boolean
  /** ?surfaceYaw — surface-derived placement yaw. */
  private readonly surfaceYawMode: boolean

  /** Last placement hit the PREVIEW resolved (depth mode), with its
   *  timestamp. The tap reuses it while fresh so a depth-sample
   *  dropout at the tap instant cannot silently swap the target to
   *  the plane hit — the wheel lands exactly where the preview stood
   *  (2026-08-20 field round 4). */
  private lastPreviewHit: { hit: HitResult; atMs: number } | null = null
  /** Last DEPTH-sourced (grounded) resolution — the sticky-depth hold
   *  consulted when the depth sample drops out mid-burst. */
  private lastDepthChoice: { hit: HitResult; atMs: number } | null = null

  private template: THREE.Group | null = null
  // Multiplicative scale factor that normalises the template's max
  // bounding-box dimension to templateDiameterMeters (default 0.6m).
  // The normalisation factor is applied to template.scale during
  // setWheelModel and travels with every clone(true) — preview and
  // placed wheels alike. It is deliberately NOT stored: reading it back
  // in applyTransform is what applied it twice to placed wheels
  // (2026-08-22). The scaled template is the single carrier.
  private readonly nodes = new Map<WheelId, PlacedNode>()
  private selectedId: WheelId | null = null

  private wallMesh: THREE.Mesh | null = null
  // Preview wheel — camera-locked clone of the template shown before first
  // placement, so the user sees the actual wheel where it will land
  // (replaces the abstract reticle/target graphic).
  private previewWheel: THREE.Group | null = null
  // Face radius of the current preview build — the dimension-based
  // contact-lift fallback for the preview path.
  private previewFaceRadiusM = 0
  /** Clock (injectable for deterministic tween tests). */
  private readonly now: () => number
  /** Preview tween clock state — null until the preview first shows. */
  private previewTweenLastMs: number | null = null
  /** Aiming state: true while the preview+gauge flow is live. Armed at
   *  start; DISARMED by placing a wheel; re-armed by beginPlacement()
   *  (the "+" button) or by deleting the last wheel. Single writer for
   *  "is the user placing right now" — controller + gauge both read it. */
  private aimingArmed = true

  // Scene lighting — analytic fill lights + IBL environment map + WebXR
  // light estimation. Metallic PBR wheels render near-black without an
  // environment map to reflect (analytic lights alone don't light metal);
  // SceneLighting owns the neutral RoomEnvironment baseline plus ARCore
  // light estimation. See scene-lighting.ts.
  private readonly lighting: SceneLighting

  // Dev-only hit-indicator grid — visualises the current ARCore hit-test
  // position each frame so the developer can see WHICH real-world plane
  // the raycast is snapping to. Created only when wallDebugVisible is
  // true; production bundle never sees it. Scene child, not attached to
  // the virtual wall — as ARCore identifies larger / different planes,
  // the grid follows the driver.hitTest result to whichever plane the
  // camera-forward ray hits closest.
  private hitIndicatorGrid: THREE.GridHelper | null = null

  private sessionStateValue: ARSessionState = arSessionState.initializing()
  private readonly sessionListeners = new Set<
    (state: ARSessionState) => void
  >()

  private idCounter = 0

  constructor(config: WebXrWheelRendererConfig) {
    this.driver = config.driver
    this.gltfParser = config.gltfParser
    this.scene = config.scene ?? new THREE.Scene()
    this.camera =
      config.camera ?? new THREE.PerspectiveCamera(60, 1, 0.01, 1000)
    this.canvas = config.canvas
    this.lerpAlpha = config.lerpAlpha ?? DEFAULT_LERP_ALPHA
    this._defaultWallDistance =
      config.defaultWallDistanceMeters ?? DEFAULT_WALL_DISTANCE_M
    this.wallDebugVisible = config.wallDebugVisible ?? false
    this.templateDiameter =
      config.templateDiameterMeters ?? DEFAULT_TEMPLATE_DIAMETER_M
    this.cameraLockMode = config.cameraLockMode ?? false
    this.depthPlaceMode = config.depthPlaceMode ?? false
    this.ringPlaceMode = config.ringPlaceMode ?? false
    this.aimingPreview = config.aimingPreview ?? true
    this.selectionRingStyle = config.selectionRingStyle ?? 'android'
    this.framedPlacement = config.framedPlacement ?? false
    this.instantPlaceMode = config.instantPlaceMode ?? false
    this.depthPrepassMode = config.depthPrepassMode ?? false
    this.surfaceYawMode = config.surfaceYawMode ?? false
    this.now = config.now ?? (() => performance.now())

    // Camera must be in the scene tree so scene.updateMatrixWorld()
    // propagates its pose to camera-child objects — specifically the
    // preview wheel (ensurePreview attaches to this.camera at a fixed
    // local offset). Standalone cameras (Three.js's default pattern)
    // work fine when nothing hangs off them, but our preview does.
    // In XR8 mode the driver's onSceneReady handoff supplied a camera
    // already parented into XR8's scene; WebXR mode doesn't hand over a
    // scene, so we must ensure it here.
    this.scene.add(this.camera)

    // Scene lighting: adds analytic fill lights immediately (scene is never
    // unlit) and applies the IBL environment map + WebXR light estimation
    // once the WebGLRenderer exists (installGlLighting, from start()).
    this.lighting = new SceneLighting(this.scene)

    // Hit-indicator grid — dev-only visualiser of the current hit
    // position each frame. Small (1×1m) so it looks like a tile
    // dropped onto whichever plane the camera-forward ray hits. Starts
    // hidden until the first hit arrives.
    if (this.wallDebugVisible) {
      const grid = new THREE.GridHelper(1, 10, 0x00ff88, 0x00ff88)
      grid.name = 'autoviz-hit-indicator-grid'
      grid.visible = false
      this.scene.add(grid)
      this.hitIndicatorGrid = grid
    }
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────

  async start(): Promise<void> {
    await this.driver.start({
      canvas: this.canvas,
      threeScene: this.scene,
      threeCamera: this.camera,
      onSessionState: (state) => this.setSessionState(state),
      onSceneReady: ({ scene, camera }) => this.adoptScene(scene, camera),
      onUpdate: () => this.tick(),
      onRendererReady: (renderer) => this.lighting.installGlLighting(renderer),
    })
  }

  private adoptScene(
    nextScene: THREE.Scene,
    nextCamera: THREE.PerspectiveCamera,
  ): void {
    if (nextScene === this.scene) return
    for (const node of this.nodes.values()) {
      // Camera-lock debug nodes are parented to the camera, not the scene;
      // reparent to the new camera so they keep their screen-locked frame.
      if (this.cameraLockMode && node.group.parent === this.camera) {
        this.camera.remove(node.group)
        nextCamera.add(node.group)
      } else {
        this.scene.remove(node.group)
        nextScene.add(node.group)
      }
    }
    if (this.wallMesh != null) {
      this.scene.remove(this.wallMesh)
      nextScene.add(this.wallMesh)
    }
    if (this.previewWheel != null) {
      this.camera.remove(this.previewWheel)
      nextCamera.add(this.previewWheel)
    }
    // Reparent lights + active environment onto the new scene.
    this.lighting.reparentTo(nextScene)
    if (this.hitIndicatorGrid != null) {
      nextScene.add(this.hitIndicatorGrid)
    }
    // Ensure the new camera is in the new scene tree for the same
    // reason the constructor did it (preview-wheel matrix propagation).
    nextScene.add(nextCamera)
    this.scene = nextScene
    this.camera = nextCamera
  }

  async stop(): Promise<void> {
    await this.driver.stop()
    this.clearAllWheels()
    this.removePreview()
    this.disposeWall()
  }

  // ── Model ──────────────────────────────────────────────────────────────

  async setWheelModel(input: WheelModelInput): Promise<WheelModelMeasurement> {
    const template = await this.gltfParser(input.bytes)
    // Normalise the template so its max bounding-box dimension is
    // `templateDiameter` meters (default 0.6m ≈ 24" wheel). Defends
    // against GLBs authored at non-meter scale (e.g., centimetres ×100
    // — would otherwise render screen-sized at any distance). Legacy
    // catalog technique 4.1.
    const box = new THREE.Box3().setFromObject(template)
    const size = box.getSize(new THREE.Vector3())
    const maxDim = Math.max(size.x, size.y, size.z)
    // The face as authored — the last moment the GLB's own meters exist
    // (2026-10-05: the wheel's default size when the payload has none).
    // Same face-plane read as deriveWheelDimensions.
    const faceDiameterM = Math.max(size.x, size.y)
    if (maxDim > 0) {
      template.scale.setScalar(this.templateDiameter / maxDim)
    }
    this.modelUnitsPerMetre = maxDim > 0 ? this.templateDiameter / maxDim : 1
    this.template = template
    // Drop any existing preview so a wheel-swap shows the new template.
    this.removePreview()
    this.ensurePreview()
    return { faceDiameterM: faceDiameterM > 0 ? faceDiameterM : null }
  }

  /** Attaches a preview clone IFF model loaded + no wheels placed +
   *  SLAM tracking is ready. Mirrors the App-side gate that shows the
   *  "Tap to place wheel" hint only after tracking is good.
   *
   *  Production path: preview is a SCENE CHILD anchored to the
   *  raycast intersection of the camera-forward ray with the virtual
   *  wall (wall-as-local-frame from the legacy WebAR catalog). As the
   *  camera pans, the preview slides along the wall. tick() updates
   *  the position every frame via updatePreviewPosition().
   *
   *  cameraLockMode (debug): preview is a CAMERA CHILD at fixed
   *  (0, 0, -PREVIEW_DISTANCE_M) local offset — screen-locked. Used
   *  to isolate SLAM-noise jitter from renderer/GLB issues.
   */
  private ensurePreview(): void {
    if (!this.aimingPreview) return
    if (this.previewWheel != null) return
    if (this.template == null) return
    // Aiming state (2026-08-21): the nodes-count guard became an
    // explicit armed flag so "+" can re-enter the full first-wheel
    // flow (preview + gauge) with wheels already placed.
    if (!this.aimingArmed) return
    if (!this.isTrackingReady()) return
    // Wall is now committed at the FIRST TAP inside placeWheel so it
    // uses the tap-time camera pose (view-aligned snapshot placement,
    // legacy 8th-wall technique 1.1 applied at tap time). The camera-
    // locked preview does not raycast against a wall, so we no longer
    // need to commit one on tracking-ready.
    // Preview is always CAMERA-LOCKED at PREVIEW_DISTANCE_M along
    // camera-forward. As the user pans / rotates the phone, the wheel
    // moves with the camera → zero parallax while aiming, which is
    // the placement UX we want. Legacy scene-child variant with
    // per-frame world raycast was removed — the parallax it created
    // was more disorienting than the small rotation pop we now get at
    // placement time.
    // ?ringPlace: the aiming preview is a plain circle, and the wheel
    // GLB is not cloned into the scene at all until the tap commits.
    // The ring carries the template's own face radius, so it is a real
    // size target — the user matches it to the rim they can see.
    const { group: preview, faceRadiusM } = this.ringPlaceMode
      ? this.buildPlacementRingPreview()
      : this.buildPlaceableWheelGroup()
    this.previewFaceRadiusM = faceRadiusM
    if (!this.ringPlaceMode) preview.name = 'wheel-preview'
    if (this.previewMode === 'plane-anchored') {
      // Scene child at world position that gets refreshed each frame
      // from the driver's hit-test. Hidden initially until the first
      // hit lands. Result: user sees the preview attached to the real
      // plane (with AR parallax as they move).
      preview.visible = false
      this.scene.add(preview)
    } else {
      // Default 'camera-locked': parented to camera, screen-locked.
      preview.quaternion.identity()
      preview.position.set(0, 0, -PREVIEW_DISTANCE_M)
      this.camera.add(preview)
    }
    this.previewWheel = preview
  }

  /** ?ringPlace preview: a plain circle at the template's face radius,
   *  built WITHOUT cloning the GLB. Same return shape as
   *  buildPlaceableWheelGroup so ensurePreview treats them alike. */
  private buildPlacementRingPreview(): {
    group: THREE.Group
    faceRadiusM: number
  } {
    if (this.template == null) {
      return { group: buildPlacementRing(0), faceRadiusM: 0 }
    }
    const { faceRadiusM } = deriveWheelDimensions(this.template)
    return { group: buildPlacementRing(faceRadiusM), faceRadiusM }
  }

  /** Contact-patch lift: world-space distance from a wheel group's
   *  origin down to its VISIBLE body's lowest vertex under the group's
   *  current quaternion + scale. Adding it to a target Y makes the
   *  wheel bottom rest exactly at that Y. Position terms cancel in the
   *  bbox subtraction, so a mid-lerp render position doesn't skew it.
   *  The occluder's tire-lip ring is excluded via the 'wheel-body'
   *  child — its 15% overshoot would float the wheel. Returns 0 when
   *  the group has no measurable body (test stubs, empty GLBs). */
  private measureContactLift(group: THREE.Group): number {
    const body = group.getObjectByName('wheel-body')
    if (body == null) return 0
    group.updateMatrixWorld(true)
    const bbox = new THREE.Box3().setFromObject(body)
    if (!Number.isFinite(bbox.min.y)) return 0
    return -(bbox.min.y - group.position.y)
  }

  /** Preview is a CHILD OF THE CAMERA at fixed camera-local
   *  (0, 0, -PREVIEW_DISTANCE_M). It moves with the camera in every
   *  axis — no parallax at all as the user rotates from a fixed
   *  position. The tradeoff is a small orientation pop at placement
   *  (preview follows camera rotation; placed wheel uses baseNormal
   *  = (0,0,1) so its world orientation is identity). The pop is
   *  acceptable because "no parallax while aiming" beats "no rotation
   *  step at tap-time" for placement UX. Hit-indicator (dev-only)
   *  still updates from the world-space hit position. */
  private updatePreviewPosition(): void {
    // ?depthPlace: the preview tracks the DEPTH-CHOSEN point (the
    // chooseDepthPlacement policy + grounding), so the pre-tap wheel
    // already stands where the tap will put it. Each tick's FRESH
    // resolution is cached; the tap consumes the cache via hitTest so
    // it places EXACTLY what the preview showed even when the depth
    // sample drops out at the tap instant. Flag-off keeps the raw
    // driver plane hit.
    let arHit: HitResult | null
    if (this.depthPlaceMode) {
      arHit = this.resolveDepthPlacementHit()
      this.lastPreviewHit =
        arHit == null ? null : { hit: arHit, atMs: this.now() }
    } else {
      arHit = this.driver.hitTest({ x: 0, y: 0 })
    }
    this.updateHitIndicator(arHit?.position ?? null)
    // 'camera-locked' mode: preview is parented to camera; nothing to
    // do here per-frame. 'plane-anchored' mode: preview is a scene
    // child, position updates each frame from the ARCore hit.
    if (this.previewMode !== 'plane-anchored') return
    if (this.previewWheel == null) return
    if (arHit == null) {
      if (this.depthPlaceMode && this.previewWheel.visible) {
        // HOLD-last (gauge parity): a depth dropout keeps the preview
        // where it stood instead of blinking it out.
        return
      }
      this.previewWheel.visible = false
      this.previewTweenLastMs = null
      return
    }
    // Desired pose: hit position (contact lift added below) +
    // face-camera gravity-upright orientation — same rule as
    // placeWheel, the user sees EXACTLY what the tap will produce.
    const desiredPos = new THREE.Vector3(
      arHit.position.x,
      arHit.position.y,
      arHit.position.z,
    )
    const camPos = this.camera.getWorldPosition(new THREE.Vector3())
    const raw = new THREE.Vector3().subVectors(camPos, desiredPos)
    const horizNormal = new THREE.Vector3(raw.x, 0, raw.z)
    const desiredQuat = new THREE.Quaternion()
    if (horizNormal.lengthSq() > 1e-6) {
      horizNormal.normalize()
      desiredQuat.setFromUnitVectors(new THREE.Vector3(0, 0, 1), horizNormal)
    } else {
      desiredQuat.copy(this.previewWheel.quaternion)
    }
    // Contact-patch parity with placeWheel: bottom rests at the point.
    // The lift is yaw-invariant (pure Y-rotations preserve the body's
    // vertical extent), so measuring against the current tweened
    // quaternion is exact. Dual-sourced like placement.
    // ?ringPlace: the ring is aimed at the RIM CENTRE, so it sits on the
    // point itself. The contact lift exists to rest a tire on a ground
    // line the user never sees; the ring replaces that reference, and
    // applying both would double-count.
    if (this.depthPlaceMode && !this.ringPlaceMode && !this.framedPlacement) {
      desiredPos.y +=
        resolveContactLift(
          this.measureContactLift(this.previewWheel),
          this.previewFaceRadiusM * this.previewWheel.scale.y,
        ) + TIRE_CLEARANCE_M
    }
    if (!this.previewWheel.visible || !this.depthPlaceMode) {
      // First appearance snaps (nothing to tween from); flag-off keeps
      // the legacy direct per-frame tracking.
      this.previewWheel.visible = true
      this.previewWheel.position.copy(desiredPos)
      this.previewWheel.quaternion.copy(desiredQuat)
      this.previewTweenLastMs = this.now()
      return
    }
    // Depth mode: ease toward the desired pose with the SAME curve as
    // the gauge line (2026-08-21: the raw-tracked preview read as
    // jitter next to the smooth line).
    const nowMs = this.now()
    const dtMs =
      this.previewTweenLastMs == null ? 0 : nowMs - this.previewTweenLastMs
    this.previewTweenLastMs = nowMs
    const alpha = 1 - Math.exp(-dtMs / DEPTH_TWEEN_TAU_MS)
    this.previewWheel.position.lerp(desiredPos, alpha)
    this.previewWheel.quaternion.slerp(desiredQuat, alpha)
  }

  /** Moves the dev hit-indicator grid to the given world point (or
   *  hides it if null). No-op in production (grid not created). */
  private updateHitIndicator(
    point: { x: number; y: number; z: number } | THREE.Vector3 | null,
  ): void {
    if (this.hitIndicatorGrid == null) return
    if (point == null) {
      this.hitIndicatorGrid.visible = false
      return
    }
    this.hitIndicatorGrid.visible = true
    this.hitIndicatorGrid.position.set(point.x, point.y, point.z)
    // Orient the grid so it faces the camera — GridHelper is on XZ by
    // default (horizontal), which reads well for floor planes but is
    // rotated edge-on for walls. lookAt(camera) always shows the grid
    // face-on to the viewer regardless of which real plane was hit.
    this.hitIndicatorGrid.lookAt(this.camera.getWorldPosition(new THREE.Vector3()))
  }

  private removePreview(): void {
    if (this.previewWheel == null) return
    if (this.previewWheel.parent != null) {
      this.previewWheel.parent.remove(this.previewWheel)
    }
    disposeObject(this.previewWheel)
    this.previewWheel = null
    this.previewTweenLastMs = null
  }

  // ── Wall + hit-test ────────────────────────────────────────────────────

  /**
   * Test introspection — returns the committed wall mesh, or null if not yet.
   * Production code never reads this; tests use it to assert wall pose.
   */
  _getWallMesh(): THREE.Mesh | null {
    return this.wallMesh
  }

  /** Exposes the internal scene reference so external debug layers
   *  (plane visualiser, future capture / recording modules) can add
   *  their own objects to the same tree the renderer is rendering.
   *  Not part of the WheelRenderer protocol; underscored to signal
   *  debug-only. */
  _scene(): THREE.Scene {
    return this.scene
  }

  /** Debug-only sibling of _scene() — the live XR camera, for external
   *  diagnostic layers that need billboarding (DepthDeltaVisualizer). */
  _camera(): THREE.PerspectiveCamera {
    return this.camera
  }

  private commitWall(at: HitResult): void {
    if (this.wallMesh != null) return
    const geometry = new THREE.BoxGeometry(
      WALL_WIDTH,
      WALL_HEIGHT,
      WALL_THICKNESS,
    )
    const material = new THREE.MeshBasicMaterial({
      visible: false,
      side: THREE.DoubleSide,
    })
    const mesh = new THREE.Mesh(geometry, material)
    mesh.name = 'autoviz-ato-wall'
    mesh.position.set(at.position.x, at.position.y, at.position.z)
    // Orient the wall so its +Z (thickness) points at the camera. After
    // lookAt, the wall's XY face is what wheels are raycast onto.
    mesh.lookAt(this.camera.position)

    // Note: no debug grid child on the wall anymore — the hit-indicator
    // grid (see constructor) tracks the current ARCore hit position
    // each frame and is a better visualiser of "where the raycast is
    // landing on a real plane" than a static grid on our virtual wall.

    this.scene.add(mesh)
    this.wallMesh = mesh
  }

  private disposeWall(): void {
    if (this.wallMesh == null) return
    this.scene.remove(this.wallMesh)
    this.wallMesh.geometry.dispose()
    ;(this.wallMesh.material as THREE.Material).dispose()
    this.wallMesh = null
  }

  hitTest(point: ScreenPoint): HitResult | null {
    // 'plane' mode: only real ARCore plane hits qualify → null =
    // controller refuses with toast. iOS-parity "no plane, no
    // placement" prevents wheels from floating in mid-air.
    // 'world' mode: bypass plane detection entirely — placement
    // works from any camera pose. Returns a synthetic hit at the
    // preview world position so the controller's null-gate passes;
    // placeWheel then uses camera_forward × PREVIEW_DISTANCE_M
    // via the trackMode branch.
    // ?groundLine mode: first tap consults the driver's downward-
    // angled hit-test source to derive a fixed ground Y. If that hit
    // is missing, refuse (null → controller shows toast, user
    // repositions and retries). Subsequent taps reuse the frozen
    // ground Y; XZ comes from camera_forward × PREVIEW_DISTANCE_M.
    if (this.groundLineMode) {
      if (this.groundY == null) {
        const groundHit = this.driver.getGroundHit?.() ?? null
        if (groundHit == null) return null
        this.groundY = groundHit.position.y
      }
      const cam = computeTapWorldPosition(this.camera)
      return {
        position: { x: cam.x, y: this.groundY, z: cam.z },
        normal: { x: 0, y: 1, z: 0 },
      }
    }
    if (this.trackMode === 'world') {
      const pos = computeTapWorldPosition(this.camera)
      return { position: pos, normal: { x: 0, y: 0, z: 1 } }
    }
    // ?depthPlace mode: weigh the depth-map center sample against the
    // plane hit. Depth meaningfully in FRONT of the detected plane
    // means the ray is on a real surface ARCore has no plane for —
    // the car body — and the depth point wins. Ties and depth-behind
    // cases keep the plane hit (stabler anchor). The returned hit
    // carries `source` so placeWheel picks the matching anchor kind.
    if (this.depthPlaceMode) {
      // Tap-preview consistency: reuse the hit the PREVIEW is showing
      // while fresh. Without this, a depth-sample dropout at the tap
      // instant silently fell back to the plane hit ~1m deeper than
      // the previewed spot.
      const cached = this.lastPreviewHit
      if (cached != null && this.now() - cached.atMs <= PREVIEW_HIT_REUSE_MS) {
        return cached.hit
      }
      return this.resolveDepthPlacementHit()
    }
    return this.driver.hitTest(point)
  }

  /** Fresh depth-mode placement resolution (policy + grounding). The
   *  preview refreshes this each tick and caches it; the tap consumes
   *  the cache via hitTest above. */
  private resolveDepthPlacementHit(): HitResult | null {
      const depthHit = this.driver.getDepthHit?.() ?? null
      const planeHit = this.driver.hitTest({ x: 0, y: 0 })

      // Learn the floor from the surface readings available this frame.
      // The dedicated downward ray is the intended source, but it is
      // occluded by the car body at exactly the aim the product asks
      // for, so a HORIZONTAL plane hit is harvested as a second source.
      //
      // The normal check is what makes that safe, and the height gate
      // alone is not: a hit on the car's door sits perhaps 0.8m below a
      // held phone, which is a perfectly plausible floor height, and it
      // would quietly redefine the floor as the middle of the car. A
      // vertical plane is never a floor no matter where it sits.
      this._updateFloor(this.driver.getGroundHit?.()?.position.y)
      if (isHorizontalSurface(planeHit)) this._updateFloor(planeHit?.position.y)

      const cameraPos = this.camera.getWorldPosition(new THREE.Vector3())
      let chosen = chooseDepthPlacement({
        depthHit,
        planeHit,
        cameraPos,
      })
      // PUSH THE DEPTH SAMPLE BACK. The measured point sits a
      // consistent 5-10cm proud of the real wheel's face plane, so a
      // fixed horizontal bias takes 5cm of that out (2026-08-24).
      //
      // Applied here, once, before grounding and before the sticky
      // hold records anything — so the preview and the tap see the
      // same biased point, and a held hit replayed during a dropout is
      // not pushed a second time. Depth-sourced only: the plane path
      // errs the other way and biasing it backwards would deepen the
      // bug depth placement exists to fix.
      if (chosen?.source === 'depth') {
        chosen = {
          ...chosen,
          position: pushBackFromCamera({
            point: chosen.position,
            cameraPos,
            distanceM: DEPTH_PUSH_BACK_M,
          }),
        }
      }
      // ?use_ios_ui framed placement: the point IS where the wheel's centre
      // goes (the user framed the rim) — no grounding, no sticky hold.
      if (this.framedPlacement) return chosen
      // GROUND EVERY SOURCE. A hit tells us WHERE ALONG THE RAY the
      // surface is; it does not tell us how high the wheel belongs.
      // Aiming at a car puts the sample at AIM HEIGHT on its flank —
      // true of the depth map and, just as much, of a plane hit, since
      // ARCore happily detects the car's side as a vertical plane. A
      // wheel rested at aim height floats, and a floating wheel
      // reprojects from a downward-looking camera exactly like a
      // push-back of height/tan(pitch) — the original "~30cm behind".
      //
      // So X/Z come from whatever surface was measured and Y always
      // comes from the floor estimate. The wheel then sits a fixed
      // TIRE_CLEARANCE_M above the same line the gauge draws, which is
      // the affordance the user is actually aiming with.
      //
      // Grounding used to apply to depth-sourced hits only, on the
      // assumption that a plane hit's Y is already trustworthy. It is
      // not: the 2026-08-22 field shot has the green line correct at
      // the tire contact patch and the wheel up at window height, 1.05m
      // high, because the plane hit had landed on the car's flank.
      const floorY = this.floorEstimate?.y
      if (chosen?.source === 'depth') {
        // No floor yet means the depth point is UNUSABLE — its Y is
        // unknowable and an ungrounded Y reprojects as a wrong Z, so
        // the point has no accuracy left to offer. Fall back to the
        // plane hit, which the same grounding rule below then fixes up.
        if (floorY == null) return planeHit
        const grounded = { ...chosen, position: { ...chosen.position, y: floorY } }
        // Record for the sticky-depth hold below.
        this.lastDepthChoice = { hit: grounded, atMs: this.now() }
        return grounded
      }
      // Sticky depth hold: when the depth SAMPLE is missing (dropout
      // burst) — not when a live sample legitimately lost the policy —
      // reuse the last depth-sourced point while fresh. Falling back to
      // the plane hit mid-burst flicked the target ~0.5-1m deeper. The
      // held hit was already grounded when it was recorded.
      if (
        depthHit == null &&
        this.lastDepthChoice != null &&
        this.now() - this.lastDepthChoice.atMs <= DEPTH_HOLD_MS
      ) {
        return this.lastDepthChoice.hit
      }
      // Plane-sourced fallback gets the same treatment — see above.
      if (chosen != null && floorY != null) {
        return { ...chosen, position: { ...chosen.position, y: floorY } }
      }
      return chosen
  }

  /** Folds one ground reading into the sticky floor estimate. */
  private _updateFloor(sampleY: number | null | undefined): void {
    const cameraY = this.camera.getWorldPosition(new THREE.Vector3()).y
    this.floorEstimate = updateFloorEstimate(this.floorEstimate, sampleY, cameraY)
  }

  /** The shared floor level, in world Y. Read by the placement gauge so
   *  the green line and the wheel cannot disagree about the ground —
   *  they were two readers with opposite dropout fallbacks before
   *  2026-08-22, which is what put the wheel up the back plane. Null
   *  until a plausible reading has arrived. */
  _floorY(): number | null {
    this._updateFloor(this.driver.getGroundHit?.()?.position.y)
    return this.floorEstimate?.y ?? null
  }

  /** Is the user currently AIMING (preview wheel showing)? Drives the
   *  placement gauge's visibility — the gauge belongs to the aiming
   *  state, not to placed wheels. */
  /** The point the user is currently aiming at — the SAME arbitrated
   *  hit the next tap will consume.
   *
   *  Exists so the placement gauge cannot answer "where am I aiming?"
   *  differently from the placement itself. It used to: the gauge was
   *  built on the RAW depth sample while placement consumed
   *  chooseDepthPlacement's verdict, so on a device whose depth map
   *  overshoots, the arbitration correctly discarded the reading and
   *  the line drew at it anyway (2026-08-24, Xiaomi: depth 4.21m,
   *  plane 1.24m, line three metres past the wheel).
   *
   *  Null during a dropout; the gauge holds its last pose, which is the
   *  same hold-last the placement cache applies. */
  _aimHit(): HitResult | null {
    return this.lastPreviewHit?.hit ?? null
  }

  _previewActive(): boolean {
    return this.previewWheel != null
  }

  /** Aiming armed state — placement taps are only honoured while true.
   *  See aimingArmed. */
  isAiming(): boolean {
    return this.aimingArmed
  }

  /** Re-enter the aiming flow (the "+" button): preview + gauge come
   *  back exactly as for the first wheel; the NEXT tap places. */
  beginPlacement(): void {
    this.aimingArmed = true
    this.ensurePreview()
  }

  /** Abandons an armed placement: preview down, taps stop placing.
   *  The undo for a "+" the user changed their mind about — nothing
   *  placed is touched. */
  endPlacement(): void {
    this.aimingArmed = false
    this.removePreview()
  }

  /** Debug/diagnostic: bottom-center of the SELECTED (or most recent)
   *  placed wheel's visible body, world space. Feeds the white truth
   *  tick on the ?depthHud ground gauge. null while nothing is placed. */
  _selectedWheelBottom(): { x: number; y: number; z: number } | null {
    const node =
      (this.selectedId != null ? this.nodes.get(this.selectedId) : null) ??
      Array.from(this.nodes.values()).pop() ??
      null
    if (node == null) return null
    const body = node.group.getObjectByName('wheel-body')
    if (body == null) return null
    node.group.updateMatrixWorld(true)
    const bbox = new THREE.Box3().setFromObject(body)
    if (!Number.isFinite(bbox.min.y)) return null
    // FACE-comparable: X/Z from the group origin (the face plane — the
    // anchored point the green line showed), Y from the true body
    // bottom. Bottom-CENTER X/Z sat half the wheel width behind the
    // face and rendered as a false push-back on the gauge.
    return {
      x: node.group.position.x,
      y: bbox.min.y,
      z: node.group.position.z,
    }
  }

  hitTestWheel(point: ScreenPoint): WheelId | null {
    if (this.nodes.size === 0) return null
    this.updateMatrices()
    const ndc = this.screenToNDC(point)
    const raycaster = new THREE.Raycaster()
    raycaster.setFromCamera(ndc, this.camera)
    let closestId: WheelId | null = null
    let closestDist = Infinity
    for (const node of this.nodes.values()) {
      const intersections = raycaster.intersectObject(node.group, true)
      if (
        intersections.length > 0 &&
        intersections[0].distance < closestDist
      ) {
        closestDist = intersections[0].distance
        closestId = node.id
      }
    }
    return closestId
  }

  /** ?surfaceYaw: yaw normal derived from the struck surface, or null
   *  when nothing derivable (caller keeps the view-aligned fallback).
   *  Depth-sourced hits read the driver's neighbourhood chord — their
   *  HitResult.normal is meaningless. Everything else reads the
   *  driver's REAL pose-derived surface normal, never at.normal, which
   *  is a hardcoded placeholder on the plane path. The one event per
   *  placement is the field observability for the flagged experiment. */
  private deriveSurfaceYawNormal(
    at: HitResult,
    point: THREE.Vector3,
    cameraPos: THREE.Vector3,
  ): { x: number; y: number; z: number } | null {
    const pointV = { x: point.x, y: point.y, z: point.z }
    const cameraV = { x: cameraPos.x, y: cameraPos.y, z: cameraPos.z }
    let source: 'depth-chord' | 'plane-normal' | 'none' = 'none'
    let derived: { x: number; y: number; z: number } | null = null
    if (at.source === 'depth') {
      const chord = this.driver.getDepthChord?.() ?? null
      if (chord != null) {
        source = 'depth-chord'
        derived = yawNormalFromChord({
          left: chord.left,
          right: chord.right,
          cameraPos: cameraV,
          point: pointV,
        })
      }
    } else {
      const normal = this.driver.getSurfaceHitNormal?.() ?? null
      if (normal != null) {
        source = 'plane-normal'
        derived = yawNormalFromSurfaceNormal({
          normal,
          cameraPos: cameraV,
          point: pointV,
        })
      }
    }
    atoLog.event('surface_yaw_placement', {
      source,
      derived: derived != null,
    })
    return derived
  }

  placeWheel(at: HitResult, scale: WheelScale): PlacedWheel {
    if (this.template == null) {
      throw new Error('no wheel model loaded; call setWheelModel first')
    }
    // Placement point + anchor choice depend on trackMode:
    //   'plane' (default) — TARGET = at.position (real ARCore plane
    //     hit), anchor = plane-attached (XRHitTestResult.createAnchor).
    //     Wheel visually animates from preview → target.
    //   'world' — TARGET = previewWorldPosition (camera_forward × 2m),
    //     anchor = manufactured world anchor. Preview and target
    //     coincide → no animation, but position estimate is simpler
    //     and (per user UAT) visibly more stable under harsh light.
    // Preview world position is used either as the animation start
    // ('plane') or as the placement point itself ('world').
    const previewWorldPosition = computeTapWorldPosition(this.camera)
    if (this.trackMode === 'world') {
      at = { position: previewWorldPosition, normal: at.normal }
    }
    // ?groundLine mode: hitTest has already returned a synthesized
    // hit at (cameraForwardXZ, groundY). Force a manufactured world
    // anchor at that exact point — skip the plane-attached path.
    const useGroundLineAnchor = this.groundLineMode && this.groundY != null

    // Legacy commit-wall step is kept only for the camera-lock DEBUG
    // path; production (non-debug) placement no longer needs a virtual
    // wall — the plane-attached XRAnchor is the source of truth.
    if (!this.cameraLockMode && this.wallMesh == null) {
      this.commitWall(at)
    }

    const id = `wheel-${++this.idCounter}`
    const { group, faceRadiusM, widthM } = this.buildPlaceableWheelGroup()
    group.name = `wheel-${id}`
    // Silence unused warning — width is exposed for tests / future use.
    void widthM

    const basePosition = new THREE.Vector3(
      at.position.x,
      at.position.y,
      at.position.z,
    )
    // View-aligned + gravity-aligned snapshot placement:
    //   (1) VIEW ALIGNMENT — the base normal points from the placement
    //       spot back toward the CURRENT camera world position. Wheel
    //       face aims at the camera at tap time.
    //   (2) GRAVITY ALIGNMENT (upright) — the normal is then projected
    //       onto the horizontal plane (Y=0). Setting the wheel's local
    //       +Z to a horizontal vector via setFromUnitVectors gives a
    //       PURE Y-axis rotation, which keeps the wheel's local +Y
    //       aligned with world +Y — the wheel stands upright
    //       regardless of camera pitch.
    // Fallbacks (in order):
    //   - camera-toward-placement projection is zero (camera straight
    //     above/below the placement point) → use camera's yaw-derived
    //     forward on the XZ plane.
    //   - even that is zero (camera looking exactly along world Y) →
    //     use world +Z as the ultimate default.
    const cameraWorldPos = this.camera.getWorldPosition(new THREE.Vector3())
    const rawNormal = new THREE.Vector3()
      .subVectors(cameraWorldPos, basePosition)
    const baseNormal = new THREE.Vector3(rawNormal.x, 0, rawNormal.z)
    if (baseNormal.lengthSq() < 1e-6) {
      // Camera straight above / below placement — pick the camera's
      // yaw direction: project camera-forward onto XZ.
      const camForward = new THREE.Vector3(0, 0, -1).applyQuaternion(
        this.camera.quaternion,
      )
      // Wheel face should look back at camera → negate forward, project.
      baseNormal.set(-camForward.x, 0, -camForward.z)
      if (baseNormal.lengthSq() < 1e-6) {
        baseNormal.set(0, 0, 1)
      }
    }
    baseNormal.normalize()

    // ?surfaceYaw: replace the view-aligned yaw with one derived from
    // the struck surface when the data supports it. The view-aligned
    // vector above stays the fallback for every null, so flag-off and
    // no-data placements are bit-for-bit the pinned behaviour.
    if (this.surfaceYawMode) {
      const derived = this.deriveSurfaceYawNormal(at, basePosition, cameraWorldPos)
      if (derived != null) {
        baseNormal.set(derived.x, derived.y, derived.z)
      }
    }

    // Anchor path picks up trackMode:
    //   'plane' → prefer plane-attached (ARCore glues to the tracked
    //     plane). Falls back to manufactured world anchor for legacy
    //     drivers or if plane path returns null.
    //   'world' → skip plane path entirely, use manufactured world
    //     anchor at the (already-overridden) previewWorldPosition.
    let anchorId: string | null = null
    if (
      this.trackMode === 'plane' &&
      !useGroundLineAnchor &&
      // Depth-sourced hits MUST NOT use a plane-attached anchor: the
      // nearest detected plane is the wall/ground BEHIND the car —
      // exactly the wrong plane. They fall through to the manufactured
      // world anchor at the depth point below.
      at.source !== 'depth' &&
      this.driver.createPlaneAttachedAnchor != null
    ) {
      const planeAnchor = this.driver.createPlaneAttachedAnchor()
      if (planeAnchor != null) {
        anchorId = planeAnchor.id
      }
    }
    if (anchorId == null) {
      anchorId = this.driver.createAnchor?.(at) ?? null
    }

    const node: PlacedNode = {
      id,
      group,
      basePosition,
      baseNormal,
      scale,
      adjustments: { ...DEFAULT_MANUAL_WHEEL_ADJUSTMENTS },
      fixedPos: new THREE.Vector3(),
      anchorId,
      faceRadiusM,
      modelUnitsPerMetre: this.modelUnitsPerMetre,
      anchorLanded: false,
    }
    this.applyTransform(node)
    // Field-triage tap record: what was placed, from which sensor, and
    // where the transform actually put it — including BOTH contact-lift
    // estimators so a failed bbox measurement is visible ([placement]
    // grep pairs this with the anchor-landed line above).
    const bboxLift = this.measureContactLift(group)
    const dimLift = faceRadiusM * group.scale.y
    // eslint-disable-next-line no-console
    console.log(
      `[placement] tap source=${at.source ?? 'plane'} point=(${at.position.x.toFixed(3)}, ${at.position.y.toFixed(3)}, ${at.position.z.toFixed(3)}) fixedPos=(${node.fixedPos.x.toFixed(3)}, ${node.fixedPos.y.toFixed(3)}, ${node.fixedPos.z.toFixed(3)}) lift=${resolveContactLift(bboxLift, dimLift).toFixed(3)} bboxLift=${bboxLift.toFixed(3)} dimLift=${dimLift.toFixed(3)} anchor=${anchorId ?? 'none'}`,
    )
    // Animation start: render position begins at the PREVIEW's world
    // position (camera_forward × PREVIEW_DISTANCE_M at tap time), NOT
    // at fixedPos. The tick lerp (lerpAlpha=0.1) then smoothly moves
    // it toward fixedPos over ~500 ms so the transition from preview
    // to the real plane depth is animated rather than snapping.
    // ?instantPlace kills the sweep: the wheel appears at its final
    // pose immediately (field-triage switch for perceived drift).
    if (this.instantPlaceMode) {
      group.position.copy(node.fixedPos)
    } else {
      group.position.set(
        previewWorldPosition.x,
        previewWorldPosition.y,
        previewWorldPosition.z,
      )
    }
    // Tearing down the preview at commit time — the placed wheel takes
    // its role from here on; aiming is over until "+" re-arms it.
    this.removePreview()
    this.aimingArmed = false
    if (this.cameraLockMode) {
      // DEBUG baseline: parent to the live camera; wheel renders at the
      // SAME screen location every frame regardless of camera pose. If
      // jitter persists here, the cause is the GLB / renderer — NOT SLAM.
      // Local offset: PREVIEW_DISTANCE_M forward, viewport center (matches preview).
      this.camera.add(group)
      group.position.set(0, 0, -PREVIEW_DISTANCE_M)
      group.quaternion.identity()
    } else {
      this.scene.add(group)
    }
    this.nodes.set(id, node)

    return {
      id,
      position: vec3FromTHREE(basePosition),
      normal: vec3FromTHREE(baseNormal),
      scale,
    }
  }

  removeWheel(id: WheelId): void {
    const node = this.nodes.get(id)
    if (node == null) return
    this.scene.remove(node.group)
    disposeObject(node.group)
    if (node.anchorId != null) this.driver.releaseAnchor?.(node.anchorId)
    this.nodes.delete(id)
    if (this.selectedId === id) this.selectedId = null
    // Once the scene is empty again, RE-ARM aiming so the user has the
    // same target affordance as before their first placement. With
    // wheels remaining, aiming stays as-is (the "+" button arms it).
    if (this.nodes.size === 0) this.aimingArmed = true
    this.ensurePreview()
  }

  clearAllWheels(): void {
    for (const node of this.nodes.values()) {
      // Camera-locked wheels live on the camera, not the scene.
      if (node.group.parent != null) {
        node.group.parent.remove(node.group)
      } else {
        this.scene.remove(node.group)
      }
      disposeObject(node.group)
      if (node.anchorId != null) this.driver.releaseAnchor?.(node.anchorId)
    }
    this.nodes.clear()
    this.selectedId = null
    // Wall persists across clearAllWheels (legacy behavior: same surface,
    // new wheels). To dispose the wall, call stop().
    // Empty scene → re-arm aiming and bring the preview back.
    this.aimingArmed = true
    this.ensurePreview()
  }

  getWheels(): readonly PlacedWheel[] {
    const out: PlacedWheel[] = []
    for (const node of this.nodes.values()) {
      out.push({
        id: node.id,
        position: vec3FromTHREE(node.basePosition),
        normal: vec3FromTHREE(node.baseNormal),
        scale: node.scale,
      })
    }
    return out
  }

  // ── Selection ──────────────────────────────────────────────────────────

  setSelectedWheel(id: WheelId | null): void {
    // Clear first, unconditionally: selection can move between wheels
    // without passing through null, and a ring left on the previous one
    // would say two wheels are selected.
    this.clearSelectionRing()
    if (id == null) {
      this.selectedId = null
      return
    }
    if (!this.nodes.has(id)) return
    this.selectedId = id
    // Both modes: knowing which wheel the controls act on matters just
    // as much with the arrow pad as with touch (2026-08-23 LUAT).
    this.attachSelectionRing(id)
  }

  /** Parents the ring to the wheel's own group so it inherits every
   *  pose and scale change for free — no per-frame follow needed. */
  private attachSelectionRing(id: WheelId): void {
    const node = this.nodes.get(id)
    if (node == null) return
    // faceRadiusM is captured pre-group-scale, and the group's face
    // axes are scaled uniformly, so the ring stays circular.
    node.group.add(
      this.selectionRingStyle === 'ios'
        ? buildIosSelectionArcs(node.faceRadiusM)
        : buildSelectionRing(node.faceRadiusM),
    )
  }

  private clearSelectionRing(): void {
    for (const node of this.nodes.values()) {
      const ring = node.group.getObjectByName(SELECTION_RING_NAME)
      if (ring == null) continue
      ring.removeFromParent()
      disposeSelectionRing(ring)
    }
  }

  getSelectedWheel(): WheelId | null {
    return this.selectedId
  }

  /** Runtime toggle — the adjust mode is a setting now, not a boot-time
   *  flag. Re-applies the ring so the change lands on whatever is
   *  already selected rather than waiting for the next selection. */
  /** Distance from the camera to the selected wheel, in metres.
   *
   *  ?touchUi needs it to convert a drag in pixels into a move in
   *  metres: the same finger travel must move a near wheel less than a
   *  far one, or dragging feels weightless up close and glued far away.
   *  Null when nothing is selected. */
  _selectedWheelDistanceM(): number | null {
    if (this.selectedId == null) return null
    const node = this.nodes.get(this.selectedId)
    if (node == null) return null
    const cameraPosition = this.camera.getWorldPosition(new THREE.Vector3())
    return cameraPosition.distanceTo(node.fixedPos)
  }

  // ── Adjustments + scale ───────────────────────────────────────────────

  adjustWheel(id: WheelId, adjustments: ManualWheelAdjustments): void {
    const node = this.nodes.get(id)
    if (node == null) return
    node.adjustments = { ...adjustments }
    this.applyTransform(node)
    // Land it NOW. tick() eases every wheel toward its target at
    // lerpAlpha, which is right for anchor corrections — ARCore nudges
    // a pose and the easing hides the jump. It is wrong for user input:
    // the finger has already decided where the wheel goes, so the
    // easing showed up as the wheel still creeping after the drag ended
    // (2026-08-24 LUAT). Measured at ~8cm of drift on release.
    node.group.position.copy(node.fixedPos)
  }

  getAdjustments(id: WheelId): ManualWheelAdjustments | null {
    const node = this.nodes.get(id)
    return node != null ? { ...node.adjustments } : null
  }

  /** ?use_ios_ui — the MASK band around one wheel, in REAL metres, or
   *  null for no ring (a wheel at least as large as the car's hides it
   *  unaided). Converted to the occluder's units: the model normalisation,
   *  then the wheel's live face scale divided back out so the band draws
   *  at its real width on a resized wheel. Rebuilt per call; the caller
   *  calls it when the answer or the committed size changes. Never called
   *  flag-off, where the proportional tire ring stays as built. */
  setWheelBand(id: WheelId, band: { solidM: number; fadeM: number } | null): void {
    const node = this.nodes.get(id)
    if (node == null) return
    const occluder = node.group.getObjectByName('wheel-barrel-occluder')
    if (occluder == null) return
    for (const existing of occluder.children.filter((c) => c.name === 'tire-lip-ring')) {
      existing.removeFromParent()
      disposeObject(existing)
    }
    if (band == null) return
    const toLocal =
      node.modelUnitsPerMetre / (node.scale.face * (node.adjustments.scaleMultiplier ?? 1))
    occluder.add(
      buildTireBandRing({
        faceRadiusM: node.faceRadiusM,
        solidM: band.solidM * toLocal,
        fadeM: band.fadeM * toLocal,
        depthPrepass: this.depthPrepassMode,
      }),
    )
  }

  setWheelScale(id: WheelId, scale: WheelScale): void {
    const node = this.nodes.get(id)
    if (node == null) return
    node.scale = scale
    this.applyTransform(node)
  }

  // ── Frame tick (fixedPos → render position damping) ────────────────────

  /**
   * Lerps each placed wheel's render position toward its canonical
   * fixedPos at `lerpAlpha`. RealXR8Driver calls this every XR8 frame via
   * the onUpdate hook; tests invoke it manually. Quaternion and scale
   * apply immediately (no smoothing).
   */
  /** Give every placed wheel its own depth pass.
   *
   *  The barrel mask writes depth with no colour, so it culls whatever
   *  sits behind it — including the OTHER wheel, and over an area wider
   *  than the wheel it belongs to. Ordering wheels far to near and
   *  clearing depth before each one confines a mask to its own wheel
   *  while keeping the nearest wheel on top (2026-08-23 LUAT).
   *
   *  Re-evaluated per frame because "which wheel is nearer" changes as
   *  the user walks around. */
  /** No selection highlight while a placement is armed.
   *
   *  Placement deselects, so usually nothing is ringed — but tapping a
   *  wheel mid-aim selects it without disarming, which left a green ring
   *  competing with the wheel being placed. During aiming the only green
   *  in the scene should be what you are lining up (2026-08-23 LUAT). */
  private updateSelectionRingVisibility(): void {
    const hidden = this.aimingArmed
    for (const node of this.nodes.values()) {
      const ring = node.group.getObjectByName(SELECTION_RING_NAME)
      if (ring != null) ring.visible = !hidden
    }
  }

  private updateWheelRenderOrder(): void {
    const cameraPosition = this.camera.getWorldPosition(new THREE.Vector3())

    // The PREVIEW counts as a wheel here. It carries the same barrel
    // mask, so while aiming it was punching the same hole in already
    // placed wheels — the "occlusion still occurs during placement"
    // half of the 2026-08-23 report.
    const groups: Array<{ id: string; distance: number; group: THREE.Object3D }> =
      Array.from(this.nodes.values()).map((node) => ({
        id: String(node.id),
        distance: cameraPosition.distanceTo(node.fixedPos),
        group: node.group,
      }))
    const preview = this.previewWheel
    if (preview != null && preview.visible) {
      groups.push({
        id: 'preview',
        distance: cameraPosition.distanceTo(
          preview.getWorldPosition(new THREE.Vector3()),
        ),
        group: preview,
      })
    }
    if (groups.length < 2) return

    sortWheelsFarToNear(groups).forEach(({ group }, position) => {
      const slots = renderOrderForWheel(position)
      group.traverse((child) => {
        if (child.name === 'barrel-mask-cylinder') {
          child.renderOrder = slots.mask
          // Wipe the depth written by earlier wheels so this mask can
          // only reach its own. onBeforeRender runs immediately before
          // this object's draw call, which is exactly the seam between
          // one wheel's pass and the next.
          //
          // Under ?depthPrepass the clears are DROPPED. They are what
          // made bodies stack by painter order while the gradient
          // stacked by depth — two rules, two different pop moments.
          // With one pre-pass depth buffer both resolve the same way.
          child.onBeforeRender = this.depthPrepassMode
            ? () => undefined
            : (glRenderer) => {
                glRenderer.clearDepth()
              }
        } else if (child.name === 'barrel-depth-restamp') {
          // Fixed slot at the tail of the opaque queue — NOT part of
          // this wheel's block, because its whole purpose is to run
          // after every wheel has finished clearing and drawing.
          child.renderOrder = DEPTH_RESTAMP_RENDER_ORDER
        } else if (child.name === SELECTION_RING_NAME) {
          child.renderOrder = slots.overlay
        } else if (child.name === 'tire-lip-ring') {
          child.renderOrder = slots.gradient
        } else if (child !== group) {
          child.renderOrder = slots.body
        }
      })
    })
  }

  tick(): void {
    this.updateSelectionRingVisibility()
    this.updateWheelRenderOrder()

    // Preview follows the camera-forward raycast across the wall every
    // frame — kept outside the cameraLockMode early-return so it fires
    // in both modes (no-op inside if the preview is camera-locked or
    // the wall isn't committed yet).
    this.updatePreviewPosition()

    // Camera-lock debug nodes have a fixed camera-local position; lerping
    // toward fixedPos (which is in WORLD coords) would corrupt the local
    // offset and drag the wheel away from the screen-lock baseline.
    if (this.cameraLockMode) return
    if (this.nodes.size === 0) return
    for (const node of this.nodes.values()) {
      this.syncNodeToAnchor(node)
      node.group.position.lerp(node.fixedPos, this.lerpAlpha)
    }
  }

  /** For anchored nodes: resample the anchor's current world position
   *  and act on it under a SNAP-TO-FIRST policy — best UX for
   *  stationary-car placement:
   *   - null pose BEFORE first resolve → stay visible (the async
   *     plane-attached anchor is still landing; the wheel is
   *     animating from preview → target and must be seen).
   *   - null pose AFTER first resolve → hide (real tracking gap;
   *     the reference-space is drifting under us and continuing
   *     to render at the stored basePosition would make the wheel
   *     swim away from its real-world spot).
   *   - FIRST non-null pose → adopt it as basePosition, flip
   *     anchorLanded to true, applyTransform so fixedPos snaps
   *     to the real plane depth (this is what the placement lerp
   *     animates toward).
   *   - subsequent non-null poses → visible flag on, but
   *     basePosition is FROZEN. ARCore may keep updating the
   *     anchor pose (plane merges, re-estimation under harsh
   *     light), and the anchor may drift centimetres; we
   *     deliberately ignore that so the wheel doesn't jump. The
   *     car isn't moving, so we don't need to follow ARCore's
   *     world-model corrections after the wheel is committed.
   *  No-op when the node has no anchor (legacy driver or opt-out). */
  private syncNodeToAnchor(node: PlacedNode): void {
    if (node.anchorId == null) return
    const pos = this.driver.getAnchorPosition?.(node.anchorId)
    if (pos == null) {
      node.group.visible = !node.anchorLanded
      return
    }
    node.group.visible = true
    if (node.anchorLanded) return
    node.anchorLanded = true
    const dx = pos.x - node.basePosition.x
    const dy = pos.y - node.basePosition.y
    const dz = pos.z - node.basePosition.z
    if (dx * dx + dy * dy + dz * dz < 1e-6) return
    // SNAP-TO-TAP: the tap pose is authoritative and the anchor NEVER
    // relocates the wheel. We only note the divergence for triage.
    //
    // This used to rewrite basePosition to the anchor's pose. For a
    // plane-attached anchor that pose is ARCore's own hit — ungrounded,
    // at aim height on whatever plane it found. Aim at a car and the
    // anchor sits on the flank, so the wheel jumped up the panel AND
    // grew, being nearer the lens: the 2026-08-22 "looked perfect, then
    // on tap it scaled up and floated up the plane".
    //
    // Nothing is lost. Post-landing shifts were already ignored
    // (SNAP-TO-FIRST), so this landing rewrite was the only positional
    // correction anchors ever applied. The anchor still earns its keep
    // by gating visibility through tracking gaps above.
    // eslint-disable-next-line no-console
    console.log(
      `[placement] anchor landed ${Math.sqrt(dx * dx + dy * dy + dz * dz).toFixed(3)}m from tap point (dx=${dx.toFixed(3)} dy=${dy.toFixed(3)} dz=${dz.toFixed(3)}) — IGNORED, tap pose holds`,
    )
  }

  /** Test-only accessor. Returns the THREE.Group of the first placed
   *  wheel, for visibility assertions during tracking-gap tests. */
  _debugFirstGroup(): THREE.Group | null {
    const first = this.nodes.values().next().value
    return first?.group ?? null
  }

  /** Live-swap the placement tracking mode. Affects placeWheel only —
   *  existing placed wheels keep whichever anchor they were created
   *  with. Used by the ?trackToggle debug button. */
  setTrackMode(mode: 'plane' | 'world'): void {
    this.trackMode = mode
  }

  getTrackMode(): 'plane' | 'world' {
    return this.trackMode
  }

  /** Live-swap preview target mode. Existing preview is torn down and
   *  rebuilt under the new mode; placed wheels are unaffected. */
  setPreviewMode(mode: 'camera-locked' | 'plane-anchored'): void {
    this.previewMode = mode
    this.removePreview()
    this.ensurePreview()
  }

  getPreviewMode(): 'camera-locked' | 'plane-anchored' {
    return this.previewMode
  }

  /** ?groundLine=1 mode. When enabled, the first placement raycasts
   *  through the driver's downward-angled hit-test source to derive a
   *  fixed ground Y; subsequent placements land at that Y. Disabling
   *  clears the frozen Y so a re-enable can re-calibrate. */
  setGroundLineMode(enabled: boolean): void {
    this.groundLineMode = enabled
    if (!enabled) this.groundY = null
  }

  getGroundLineMode(): boolean {
    return this.groundLineMode
  }

  /** Test / UAT accessor: the calibrated ground Y (null before the
   *  first ?groundLine tap). */
  getGroundY(): number | null {
    return this.groundY
  }

  /** Clones the template, wraps it in an outer group whose LOCAL
   *  origin coincides with the wheel's FACE PLANE (rather than the
   *  body's bounding-box center as authored in the GLB), and attaches
   *  the barrel occluder. Result: when this group's world position is
   *  set to a hit point, the wheel's face touches that point — the
   *  natural placement for a wall-mounted or floor-lying display
   *  wheel. Also used for the preview so the pre-tap look matches
   *  the placed look. */
  private buildPlaceableWheelGroup(): {
    group: THREE.Group
    faceRadiusM: number
    widthM: number
  } {
    if (this.template == null) {
      throw new Error('no wheel model loaded; call setWheelModel first')
    }
    const outerGroup = new THREE.Group()
    const body = this.template.clone(true)
    // Shift the wheel body along local -Z so its FACE plane (bounding-
    // box max Z) sits at the outer group's origin. The GLB is typically
    // authored with its origin at the body's geometric center; face is
    // at +halfWidth in that convention.
    const bodyBox = new THREE.Box3().setFromObject(body)
    body.position.z = -bodyBox.max.z
    // Named so contact-patch anchoring can measure the VISIBLE wheel's
    // extent — the occluder's tire-lip ring overshoots the real rim by
    // 15% and would float the wheel if included in the bbox.
    body.name = 'wheel-body'
    outerGroup.add(body)
    const { faceRadiusM, widthM, backdropZM } = deriveWheelDimensions(
      this.template,
    )
    outerGroup.add(
      createWheelBarrelOccluder(
        faceRadiusM,
        widthM,
        this.depthPrepassMode,
        backdropZM,
      ),
    )
    return { group: outerGroup, faceRadiusM, widthM }
  }

  // ── Session state stream ──────────────────────────────────────────────

  getSessionState(): ARSessionState {
    return this.sessionStateValue
  }

  subscribeSessionState(
    listener: (state: ARSessionState) => void,
  ): () => void {
    this.sessionListeners.add(listener)
    return () => {
      this.sessionListeners.delete(listener)
    }
  }

  private setSessionState(state: ARSessionState): void {
    const wasReady = this.isTrackingReady()
    this.sessionStateValue = state
    const isReady = this.isTrackingReady()
    // Transition gate: preview wheel materialises only once tracking is
    // ready, and is dropped if tracking degrades.
    if (!wasReady && isReady) this.ensurePreview()
    else if (wasReady && !isReady) this.removePreview()
    for (const l of Array.from(this.sessionListeners)) l(state)
  }

  private isTrackingReady(): boolean {
    const k = this.sessionStateValue.kind
    return (
      k === 'ready' ||
      k === 'tracking' ||
      k === 'placing' ||
      k === 'placed'
    )
  }

  // ── Internal: transform math ──────────────────────────────────────────

  private applyTransform(node: PlacedNode): void {
    const { group, basePosition, baseNormal, scale, adjustments } = node

    const baseQuat = new THREE.Quaternion().setFromUnitVectors(
      new THREE.Vector3(0, 0, 1),
      baseNormal,
    )

    const tangent = perpendicularTangent(baseNormal)
    const worldY = new THREE.Vector3(0, 1, 0)

    const finalPosition = new THREE.Vector3()
      .copy(basePosition)
      .addScaledVector(baseNormal, adjustments.depthOffset)
      .addScaledVector(worldY, adjustments.heightOffset)
      .addScaledVector(tangent, adjustments.horizontalOffset)

    const rotationAroundNormal = new THREE.Quaternion().setFromAxisAngle(
      baseNormal,
      adjustments.rotationAngle,
    )
    const tiltAroundTangent = new THREE.Quaternion().setFromAxisAngle(
      tangent,
      adjustments.tiltAngle,
    )
    const yawAroundY = new THREE.Quaternion().setFromAxisAngle(
      worldY,
      adjustments.yawAngle,
    )

    const finalQuat = new THREE.Quaternion()
      .multiplyQuaternions(yawAroundY, baseQuat)
      .multiply(rotationAroundNormal)
      .multiply(tiltAroundTangent)

    const axisVec = wheelScaleAxisVector(scale)

    group.quaternion.copy(finalQuat)
    // The normalisation factor is ALREADY baked into the cloned body —
    // setWheelModel scales the template itself, and
    // buildPlaceableWheelGroup clones that scaled template. So the outer
    // group carries only the WheelScale axis-vector and the user's
    // multiplier; folding the factor in here applied it twice.
    //
    // That double-application is the "on tap the wheel scaled up"
    // half of the 2026-08-22 report. The preview leaves its outer group
    // at scale 1 and so rendered at the correct size, while the placed
    // wheel came out a full factor larger — 1.2x for a GLB authored
    // around 0.5m against the 0.6m target diameter, which is what the
    // field shot shows. The old comment here reasoned that the factor
    // was needed for preview↔placed parity; it produced exactly the
    // mismatch it set out to prevent.
    //
    // User's live scaleMultiplier (from JoystickPad Scale mode). 1.0 =
    // no adjustment; 1.15 = wheel appears 15 % larger, etc.
    const m = adjustments.scaleMultiplier ?? 1
    group.scale.set(axisVec[0] * m, axisVec[1] * m, axisVec[2] * m)

    // Contact-patch anchoring (?depthPlace, 2026-08-20 field obs 3):
    // the wheel BOTTOM rests at the measured point, not the face
    // center — a wheel is judged against the ground line, and the
    // depth gauge reads 0 at the surface the tire should touch. The
    // lift is the world-space distance from the group origin down to
    // the VISIBLE body's lowest vertex under the final quat + scale
    // (measured from the live bbox: position terms cancel, so the
    // mid-lerp render position doesn't skew it). Flag-off placements
    // keep the pre-existing face-center contract byte-for-byte.
    // ?ringPlace: centred on the ring the user aimed, not lifted onto an
    // estimated ground line — see updatePreviewPosition for the reason.
    if (this.depthPlaceMode && !this.ringPlaceMode && !this.framedPlacement) {
      finalPosition.y +=
        resolveContactLift(
          this.measureContactLift(group),
          node.faceRadiusM * group.scale.y,
        ) + TIRE_CLEARANCE_M
    }

    // Position writes to fixedPos (canonical state); render position
    // lerps toward this each tick. Quaternion + scale apply directly.
    node.fixedPos.copy(finalPosition)
    if (this.lerpAlpha >= 1.0) {
      // Lerp disabled (test mode) — sync render position immediately so
      // assertions don't need to call tick() between adjusts.
      group.position.copy(finalPosition)
    }
  }

  // ── Shared raycast helpers ─────────────────────────────────────────────

  private updateMatrices(): void {
    this.camera.updateMatrixWorld(true)
    this.scene.updateMatrixWorld(true)
  }

  private screenToNDC(point: ScreenPoint): THREE.Vector2 {
    const w = this.canvas?.clientWidth ?? this.canvas?.width ?? 1
    const h = this.canvas?.clientHeight ?? this.canvas?.height ?? 1
    return new THREE.Vector2((point.x / w) * 2 - 1, -(point.y / h) * 2 + 1)
  }
}

// ── helpers ─────────────────────────────────────────────────────────────

function vec3FromTHREE(v: THREE.Vector3): { x: number; y: number; z: number } {
  return { x: v.x, y: v.y, z: v.z }
}

/** Computes the world position the camera-locked preview is showing:
 *  `cameraPos + cameraForward × PREVIEW_DISTANCE_M`. This is the point
 *  placeWheel uses as the placement location so tapping the preview
 *  drops the wheel EXACTLY where the user sees it — no snap to an
 *  ARCore plane at a different depth. */
function computeTapWorldPosition(
  camera: THREE.Camera,
): { x: number; y: number; z: number } {
  const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion)
  const camPos = camera.getWorldPosition(new THREE.Vector3())
  return {
    x: camPos.x + forward.x * PREVIEW_DISTANCE_M,
    y: camPos.y + forward.y * PREVIEW_DISTANCE_M,
    z: camPos.z + forward.z * PREVIEW_DISTANCE_M,
  }
}


function perpendicularTangent(normal: THREE.Vector3): THREE.Vector3 {
  const worldY = new THREE.Vector3(0, 1, 0)
  if (Math.abs(normal.dot(worldY)) > 0.999) {
    return new THREE.Vector3(1, 0, 0)
  }
  return new THREE.Vector3().crossVectors(normal, worldY).normalize()
}

function disposeObject(object: THREE.Object3D): void {
  object.traverse((child) => {
    const mesh = child as THREE.Mesh
    if (mesh.isMesh) {
      mesh.geometry?.dispose?.()
      const material = mesh.material
      if (Array.isArray(material)) {
        for (const m of material) m.dispose?.()
      } else if (material != null) {
        ;(material as THREE.Material).dispose?.()
      }
    }
  })
}
